// Campaign Banner Carousel Component
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useCampaign } from '../../context/CampaignContext';
import { CAMPAIGN_POSITIONS } from '../../data/campaignModels';
import FreeTrialPromotionModal from '../Marketplace/FreeTrialPromotionModal';
import { normalizeLanguageCode } from '../../utils/campaignUtils';

const CampaignBannerCarousel = ({ positionId = CAMPAIGN_POSITIONS.HOMEPAGE_TOP, userStoreId, fallback = null }) => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const { campaigns, loadCampaignsForPosition, handleCampaignView, handleCampaignClick } = useCampaign();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  // Load campaigns when i18n is ready and language changes
  useEffect(() => {
    // Wait for i18n to be initialized
    if (!i18n.isInitialized) {
      return;
    }
    
    const rawLanguage = i18n.language || 'th';
    const normalizedLanguage = normalizeLanguageCode(rawLanguage);
    console.log('[BannerCarousel] Loading campaigns for position:', positionId, 'language:', normalizedLanguage);
    loadCampaignsForPosition(positionId, normalizedLanguage).then((loadedCampaigns) => {
      console.log('[BannerCarousel] Loaded campaigns:', loadedCampaigns?.length || 0, 'for position:', positionId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId, i18n.language, i18n.isInitialized]); // Depend on i18n.language and i18n.isInitialized
  
  // Removed console.log to prevent infinite loop

  // Auto-rotate banners
  useEffect(() => {
    if (campaigns.length > 1) {
      const timer = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % campaigns.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [campaigns.length]);

  // Track view when banner is displayed
  useEffect(() => {
    if (campaigns.length > 0 && campaigns[currentIndex]) {
      handleCampaignView(campaigns[currentIndex].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, campaigns.length]); // Only depend on campaigns.length, not campaigns array

  const handleBannerClick = (campaign) => {
    try {
      console.log('[Banner] handleBannerClick called!', { campaignId: campaign?.id });
      
      // Track click (non-blocking, don't await)
      handleCampaignClick(campaign.id).catch(() => {
        // Silently fail if tracking fails
      });

      // Get banner action and link URL based on device and language
      const currentLanguage = normalizeLanguageCode(i18n.language || 'th');
      const isMobile = window.innerWidth < 768;
      const bannerConfig = isMobile
        ? (campaign.banner?.mobile?.[currentLanguage] || campaign.banner?.mobile?.th || campaign.banner?.desktop?.[currentLanguage] || campaign.banner?.desktop?.th)
        : (campaign.banner?.desktop?.[currentLanguage] || campaign.banner?.desktop?.th);
      
      const bannerAction = bannerConfig?.action || 'open-modal';
      const linkUrl = bannerConfig?.linkUrl;
      
      console.log('[Banner] Action:', bannerAction, 'Link:', linkUrl);
      
      if (bannerAction === 'open-modal') {
        // Always set selected campaign and show modal
        console.log('[Banner] Opening modal...', { campaignId: campaign.id });
        setSelectedCampaign(campaign);
        setShowPromotionModal(true);
        console.log('[Banner] Modal state updated:', { showPromotionModal: true, selectedCampaign: campaign.id });
      } else if (bannerAction === 'navigate') {
        // Navigate to URL in same tab
        if (linkUrl) {
          console.log('[Banner] Navigating to:', linkUrl);
          if (linkUrl.startsWith('/')) {
            window.location.href = linkUrl;
          } else {
            window.location.href = linkUrl;
          }
        }
      } else if (bannerAction === 'external-link') {
        // Open URL in new tab
        if (linkUrl) {
          console.log('[Banner] Opening external link:', linkUrl);
          window.open(linkUrl, '_blank', 'noopener,noreferrer');
        }
      }
    } catch (error) {
      console.error('[Banner] Error in handleBannerClick:', error);
    }
  };

  // Don't render if no campaigns
  if (!campaigns || campaigns.length === 0) {
    console.warn('[BannerCarousel] No campaigns found for position:', positionId, {
      positionId,
      campaignsCount: campaigns?.length || 0,
      hasFallback: !!fallback
    });
    // Return fallback if provided, otherwise return null
    return fallback;
  }

  console.log('[BannerCarousel] Rendering', campaigns.length, 'campaign(s) for position:', positionId);

  return (
    <>
      <div className="relative bg-white overflow-hidden" style={{ position: 'relative', zIndex: 10 }}>
        {/* Banner Container */}
        <div 
          className="flex transition-transform duration-500 ease-in-out" 
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {campaigns.map((campaign, index) => (
            <CampaignBannerSlide 
              key={campaign.id} 
              campaign={campaign}
              currentUser={currentUser} 
              userStoreId={userStoreId}
              language={normalizeLanguageCode(i18n.language || 'th')}
              onClick={() => handleBannerClick(campaign)}
            />
          ))}
        </div>
      
        {/* Dots Indicator */}
        {campaigns.length > 1 && (
          <div className="absolute bottom-2 md:bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-1.5 z-10">
            {campaigns.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full transition-all ${
                  currentIndex === index 
                    ? 'bg-white w-4 md:w-6' 
                    : 'bg-white/50 hover:bg-white/75'
                }`}
                aria-label={`Go to banner ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Free Trial Promotion Modal */}
      <FreeTrialPromotionModal
        isOpen={showPromotionModal}
        onClose={() => {
          setShowPromotionModal(false);
          setSelectedCampaign(null);
        }}
        campaign={selectedCampaign}
      />
    </>
  );
};

const CampaignBannerSlide = ({ campaign, currentUser, userStoreId, language, onClick }) => {
  const { t } = useTranslation();
  const isMobile = window.innerWidth < 768;
  
  // Get banner image URL based on device and language
  const bannerImageUrl = isMobile
    ? (campaign.banner?.mobile?.[language]?.imageUrl || campaign.banner?.mobile?.th?.imageUrl || campaign.banner?.desktop?.[language]?.imageUrl || campaign.banner?.desktop?.th?.imageUrl)
    : (campaign.banner?.desktop?.[language]?.imageUrl || campaign.banner?.desktop?.th?.imageUrl);

  const handleClick = (e) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      console.log('[BannerSlide] Click detected!', { hasOnClick: !!onClick, campaignId: campaign?.id });
      if (onClick) {
        console.log('[BannerSlide] Calling onClick handler');
        onClick();
      } else {
        console.warn('[BannerSlide] No onClick handler provided!');
      }
    } catch (error) {
      console.error('[BannerSlide] Error in handleClick:', error);
    }
  };

  // If banner has image URL, use it
  if (bannerImageUrl) {
    return (
      <div
        onClick={handleClick}
        className="w-full flex-shrink-0 h-32 md:h-48 lg:h-56 relative overflow-hidden group cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e);
          }
        }}
      >
        <img
          src={bannerImageUrl}
          alt={campaign.name?.[language] || campaign.name?.th || campaign.name?.en || 'Banner'}
          className="w-full h-full object-cover pointer-events-none"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/800x200?text=Banner';
          }}
        />
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
      </div>
    );
  }

  // Fallback: Render default banner based on campaign type
  const campaignName = campaign.name?.[language] || campaign.name?.th || campaign.name?.en || 'Campaign';
  
  if (campaign.type === 'promotion') {
    return (
      <div
        onClick={handleClick}
        className="w-full flex-shrink-0 h-32 md:h-48 lg:h-56 bg-gradient-to-r from-green-600 via-green-500 to-emerald-500 flex items-center justify-center text-white relative overflow-hidden group cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e);
          }
        }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full -ml-24 -mb-24"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-4 md:px-8 max-w-4xl w-full">
          <h2 className="text-xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-3 drop-shadow-lg">
            {campaignName}
          </h2>
          {campaign.features?.bonusCoins?.enabled && (
            <p className="text-sm md:text-lg lg:text-xl opacity-95 mb-3 md:mb-4">
              รับ Bonus Coins {campaign.features.bonusCoins.amount.toLocaleString()} เหรียญฟรี!
            </p>
          )}
        </div>
      </div>
    );
  }

  // Default banner
  return (
    <div
      onClick={handleClick}
      className="w-full flex-shrink-0 h-32 md:h-48 lg:h-56 bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center text-white relative overflow-hidden group cursor-pointer"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick(e);
        }
      }}
    >
      <div className="relative z-10 text-center px-4 md:px-8">
        <h3 className="text-lg md:text-2xl lg:text-3xl font-bold mb-1 md:mb-2 drop-shadow-lg">
          {campaignName}
        </h3>
      </div>
    </div>
  );
};

export default CampaignBannerCarousel;

